import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { setAuthItems } from "../../store/slices/projectSetup";
import styled, { keyframes } from "styled-components";
import { FaClock, FaEnvelope, FaMobileAlt } from "react-icons/fa";
import CryptoJS from "crypto-js";
import { ENDPOINTS } from "../../library/utils/constants/apiurl";
import { apiPost } from "../../library/utils/common-services/apiCall";
import { LOGIN_CRYPTO_KEY } from "../../library/utils/constants";

const FALLBACK_DOMAIN = "https://editor.printpoz.com";
const getDomain = () => {
  const h = window.location.hostname;
  if (h === "localhost" || h.includes("trycloudflare") || h === "127.0.0.1" || h === "") return FALLBACK_DOMAIN;
  return `${window.location.protocol}//${h}`;
};

// The default printpoz domain (and dev hosts, which fall back to it) doesn't
// identify a specific store, so we ask the user to enter their store's domain.
const isDefaultDomain = () => {
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h.includes("trycloudflare") ||
    h === "127.0.0.1" ||
    h === "" ||
    h === "printpoz.com" ||
    h === "editor.printpoz.com" ||
    h.endsWith(".printpoz.com")
  );
};

// Turn a raw user-typed domain ("mystore.com") into a full URL for the API.
const normalizeDomain = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";
  const withProtocol = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  return withProtocol.replace(/\/+$/, "");
};

const decryptLoginResponse = (encryptedStr) => {
  const decrypted = CryptoJS.AES.decrypt(encryptedStr, LOGIN_CRYPTO_KEY).toString(CryptoJS.enc.Utf8);
  return JSON.parse(decrypted);
};

const reloadWithNewToken = (token) => {
  const url = new URL(window.location.href);
  url.searchParams.set("u_id", token);
  window.location.href = url.toString();
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(6px);
  padding: 20px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Container = styled.div`
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid #f0f0f0;
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 480px) {
    padding: 28px 20px;
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff7ed;
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin: 0 auto 20px;
  box-shadow: 0 0 0 8px rgba(245, 158, 11, 0.08);
`;

const Title = styled.h2`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  line-height: 1.5;
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`;

const TabButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border: 1.5px solid ${(p) => (p.$active ? "#4084B5" : "#e5e7eb")};
  background: ${(p) => (p.$active ? "#EFF8FF" : "#fff")};
  color: ${(p) => (p.$active ? "#4084B5" : "#6b7280")};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #4084b5;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: #4084b5;
    box-shadow: 0 0 0 3px rgba(64, 132, 181, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 11px 16px;
  background: #4084b5;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 6px;

  &:hover:not(:disabled) {
    background: #356d96;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #ef4444;
  text-align: center;
`;

const SessionExpiredModal = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("email"); // "email" | "phone"
  const isDefault = isDefaultDomain();
  const [storeDomain, setStoreDomain] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // On the default domain the store isn't known from the hostname, so use the
  // domain the user typed; otherwise derive it from the current host.
  const resolveDomain = () => (isDefault ? normalizeDomain(storeDomain) : getDomain());

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Please enter email and password."); return; }
    if (isDefault && !normalizeDomain(storeDomain)) { setError("Please enter your store's domain."); return; }
    setLoading(true);
    setError("");
    try {
      const data = await apiPost(ENDPOINTS.authLogin, { email, password, domain: resolveDomain() }, { skipBrandId: true });
      if (data?.status === 1 && data?.items) {
        const user = typeof data.items === "string" ? decryptLoginResponse(data.items) : data.items;
        dispatch(setAuthItems(user));
        reloadWithNewToken(user.accessToken);
      } else {
        setError(data?.message || "Login failed. Please try again.");
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!phone) { setError("Please enter your phone number."); return; }
    if (isDefault && !normalizeDomain(storeDomain)) { setError("Please enter your store's domain."); return; }
    setLoading(true);
    setError("");
    try {
      const data = await apiPost(ENDPOINTS.authLogin, { phone, domain: resolveDomain() }, { skipBrandId: true });
      if (data?.status === 1) {
        dispatch(setAuthItems(data.items));
        setOtpSent(true);
        setError("");
      } else {
        setError(data?.message || "Failed to send OTP. Please try again.");
      }
    } catch (err) {
      setError("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp) { setError("Please enter the OTP."); return; }
    setLoading(true);
    setError("");
    try {
      const data = await apiPost(ENDPOINTS.verifyOTP, { phone, otp: Number(otp), domain: resolveDomain() }, { skipBrandId: true });
      if (data?.status === 1 && data?.items) {
        const user = typeof data.items === "string" ? decryptLoginResponse(data.items) : data.items;
        dispatch(setAuthItems(user));
        reloadWithNewToken(user.accessToken);
      } else {
        setError(data?.message || "OTP verification failed. Please try again.");
      }
    } catch (err) {
      setError("OTP verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay>
      <Container>
        <IconWrapper>
          <FaClock />
        </IconWrapper>
        <Title>Session Expired</Title>
        <Subtitle>
          Your session has expired. Please log in again to continue.
        </Subtitle>

        {isDefault && (
          <FormGroup>
            <Label>Store Domain</Label>
            <Input
              type="text"
              placeholder="Write your store's editor domain (e.g. mystore.com)"
              value={storeDomain}
              name="storeDomain"
              onChange={(e) => setStoreDomain(e.target.value)}
            />
          </FormGroup>
        )}

        <TabRow>
          <TabButton
            $active={activeTab === "email"}
            onClick={() => { setActiveTab("email"); setError(""); setOtpSent(false); setOtp(""); }}
          >
            <FaEnvelope size={14} /> Email Login
          </TabButton>
          <TabButton
            $active={activeTab === "phone"}
            onClick={() => { setActiveTab("phone"); setError(""); setOtpSent(false); setOtp(""); }}
          >
            <FaMobileAlt size={14} /> Phone Login
          </TabButton>
        </TabRow>

        {error && <ErrorText>{error}</ErrorText>}

        {activeTab === "email" ? (
          <form onSubmit={handleEmailLogin}>
            <FormGroup>
              <Label>Email</Label>
              <Input type="email" placeholder="Email" value={email}
                onChange={(e) => setEmail(e.target.value)} autoFocus />
            </FormGroup>
            <FormGroup>
              <Label>Password</Label>
              <Input type="password" placeholder="Password" value={password}
                onChange={(e) => setPassword(e.target.value)} />
            </FormGroup>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? "Logging in..." : "Log In"}
            </SubmitButton>
          </form>
        ) : !otpSent ? (
          <form onSubmit={handleSendOTP}>
            <FormGroup>
              <Label>Phone Number</Label>
              <Input type="tel" placeholder="Enter your phone number" value={phone}
                onChange={(e) => setPhone(e.target.value)} autoFocus />
            </FormGroup>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? "Sending OTP..." : "Send OTP"}
            </SubmitButton>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <FormGroup>
              <Label>OTP sent to {phone}</Label>
              <Input type="number" placeholder="Enter OTP" value={otp}
                onChange={(e) => setOtp(e.target.value)} autoFocus />
            </FormGroup>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? "Verifying..." : "Verify OTP"}
            </SubmitButton>
            <SubmitButton type="button" disabled={loading}
              style={{ marginTop: 8, background: "transparent", color: "#4084b5", border: "1.5px solid #4084b5" }}
              onClick={() => { setOtpSent(false); setOtp(""); setError(""); }}>
              Change Number
            </SubmitButton>
          </form>
        )}
      </Container>
    </Overlay>
  );
};

export default SessionExpiredModal;
